import { useEffect } from 'react';

import { SITE } from '@/config/site';

interface SeoProps {
  /** Page title without the brand suffix — it is appended automatically. */
  title?: string;
  description?: string;
  /** Path only, e.g. `/catalogo`. Combined with the configured site URL. */
  path?: string;
  /** Ask crawlers to skip this page (checkout, 404). */
  noIndex?: boolean;
}

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);

  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }

  el.setAttribute('content', content);
}

/**
 * Per-route document metadata.
 *
 * This is a client-rendered SPA, so these tags matter mainly for social
 * previews and for crawlers that execute JavaScript. When the store moves to
 * SSR this component is the one place that has to change.
 */
export function Seo({ title, description, path, noIndex = false }: SeoProps) {
  const fullTitle = title ? `${title} | ${SITE.name}` : `${SITE.name} — ${SITE.tagline}`;
  const desc = description ?? SITE.description;
  const url = path ? `${SITE.url}${path}` : SITE.url;

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', 'name', 'description', desc);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', desc);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', url);
    upsertMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      noIndex ? 'noindex, nofollow' : 'index, follow',
    );

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;
  }, [fullTitle, desc, url, noIndex]);

  return null;
}
